import moment from "moment";
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Badge, Button, Modal, Pagination, Table } from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import { TbLockSearch } from "react-icons/tb";
import Drawer from "../Drawer";
import CompanyQueriesForm from "../forms/CompanyQueriesForm";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  MdFilterListAlt,
  MdOutlineRemoveRedEye,
  MdKeyboardArrowDown,
  MdClose,
  MdCheckCircleOutline,
  MdOutlineRestore,
  MdOutlineBolt,
} from "react-icons/md";
import ModalDelete from "../modals/ModalDelete";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CompanyInvoiceForm from "../forms/CompanyInvoiceForm";
import { useForm } from "react-hook-form";
import SelectInput from "../inputs/SelectInput";
import AutoCompleteInput from "../inputs/Autocomplete";
import { useApplication } from "../../contexts/ApplicationContext";
import EmptyTable from "./EmptyTable";
import CompanyEditForm from "../forms/CompanyEditForm";
import DateRangeInput from "../inputs/DatePickerInput";
import { formatToReal } from "../../utils/utils";
import "react-datepicker/dist/react-datepicker.css";

const STORAGE_KEY = "adminCustomers.filters.v1";
const STORAGE_OPEN_KEY = "adminCustomers.filtersOpen.v1";
const STORAGE_VIEWS_KEY = "adminCustomers.savedViews.v1";

/* --- Micro UI helpers --- */
function Chip({ label, onClear, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${tones[tone]} whitespace-nowrap`}
    >
      {label}
      {!!onClear && (
        <button
          onClick={onClear}
          type="button"
          aria-label={`Remover filtro ${label}`}
          className="hover:opacity-70"
        >
          <MdClose size={14} />
        </button>
      )}
    </span>
  );
}

function Section({ title, children, right }) {
  return (
    <div
      className="rounded-2xl border border-transparent shadow-sm"
      style={{
        background:
          "linear-gradient(0deg,#fff,#fff) padding-box,linear-gradient(90deg,#e3ddd8,#dfe8ff) border-box",
        border: "1px solid transparent",
      }}
    >
      <div className="px-4 pt-3 pb-2 flex items-center">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#5c524b]">
          <div className="h-7 w-7 rounded-lg bg-gray-900/5 flex items-center justify-center">
            <MdFilterListAlt className="opacity-70" />
          </div>
          {title}
        </div>
        <div className="ml-auto flex items-center gap-2">{right}</div>
      </div>
      <div className="px-4 pb-3">{children}</div>
    </div>
  );
}

const AdminCustomersTable = () => {
  // URL sync
  const [searchParams, setSearchParams] = useSearchParams();

  const { userPermission, user } = useApplication();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // paginação (lendo da URL)
  const pageFromUrl = Number(searchParams.get("page") || 1);
  const [currentPage, setCurrentPage] = useState(
    Number.isNaN(pageFromUrl) || pageFromUrl <= 0 ? 1 : pageFromUrl
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => {
    setCurrentPage(page);
    const obj = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...obj, page: String(page) });
  };

  // busca (lendo da URL)
  const [searchName, setSearchName] = useState(
    searchParams.get("search") || ""
  );
  const [nameParam, setNameParam] = useState(searchParams.get("search") || "");

  const [isDrawerPermissionQueries, setIsDrawerPermissionQueries] =
    useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDrawerViewInvoice, setIsDrawerViewInvoice] = useState(false);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);

  const [isOpenModalBlock, setOpenModalBlock] = useState(false);
  const [isLoadingBlock, setIsLoadingBlock] = useState(false);
  const [typeBlock, setTypeBlock] = useState(0);

  // representante e período vindos da URL
  const [selectedRepresentative, setSelectedRepresentative] = useState({
    label: searchParams.get("representativeName") || "Todos os representantes",
    value: searchParams.get("representativeId") || "",
  });
  const [createdAtRange, setCreatedAtRange] = useState([
    searchParams.get("createdAtStart")
      ? moment(searchParams.get("createdAtStart"), "YYYY-MM-DD").toDate()
      : null,
    searchParams.get("createdAtEnd")
      ? moment(searchParams.get("createdAtEnd"), "YYYY-MM-DD").toDate()
      : null,
  ]);
  const [createdAtStart, createdAtEnd] = createdAtRange;

  // estado do colapso do filtro (persistente)
  const [isFiltersOpen, setIsFiltersOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_OPEN_KEY);
      return raw ? JSON.parse(raw) === true : true;
    } catch {
      return false;
    }
  });

  const itemsPerPage = 20;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      status: searchParams.get("status") || "",
      typePayment: searchParams.get("typePayment") || "",
      typeCustomer: searchParams.get("typeCustomer") || "",
      responsibleId: searchParams.get("responsibleId") || "",
      isOnline: searchParams.get("isOnline") || "", // '', '1', '0'
    },
  });

  // ===== Helpers de persistência =====
  const restoringFromStorageRef = useRef(false);

  const hasAnyFilterInUrl = useMemo(() => {
    const keys = [
      "search",
      "createdAtStart",
      "createdAtEnd",
      "status",
      "typePayment",
      "typeCustomer",
      "responsibleId",
      "isOnline",
      "representativeId",
    ];
    return keys.some((k) => searchParams.get(k));
  }, [searchParams]);

  // Restaura de localStorage se não houver filtros na URL
  useEffect(() => {
    if (hasAnyFilterInUrl) return; // já está vindo da URL, não sobrescrever
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      restoringFromStorageRef.current = true;

      // restaura campos do form
      setValue("status", saved.status ?? "");
      setValue("typePayment", saved.typePayment ?? "");
      setValue("typeCustomer", saved.typeCustomer ?? "");
      setValue("responsibleId", saved.responsibleId ?? "");
      setValue("isOnline", saved.isOnline ?? "");

      // datas
      const start = saved.createdAtStart
        ? moment(saved.createdAtStart, "YYYY-MM-DD").toDate()
        : null;
      const end = saved.createdAtEnd
        ? moment(saved.createdAtEnd, "YYYY-MM-DD").toDate()
        : null;
      setCreatedAtRange([start, end]);

      // representante
      setSelectedRepresentative(
        saved.selectedRepresentative ?? {
          label: "Todos os representantes",
          value: "",
        }
      );

      // aplica na URL para manter padrão do seu app
      const next = {
        page: "1",
        ...(saved.createdAtStart
          ? { createdAtStart: saved.createdAtStart }
          : {}),
        ...(saved.createdAtEnd ? { createdAtEnd: saved.createdAtEnd } : {}),
        ...(saved.status ? { status: saved.status } : {}),
        ...(saved.typePayment ? { typePayment: saved.typePayment } : {}),
        ...(saved.typeCustomer ? { typeCustomer: saved.typeCustomer } : {}),
        ...(saved.responsibleId ? { responsibleId: saved.responsibleId } : {}),
        ...(saved.isOnline ? { isOnline: saved.isOnline } : {}),
        ...(userPermission === "POWER_USER" &&
        saved.selectedRepresentative?.value
          ? {
              representativeId: saved.selectedRepresentative.value,
              representativeName: saved.selectedRepresentative.label || "",
            }
          : {}),
      };
      setSearchParams(next);
    } catch (e) {
      // ignore
    } finally {
      setTimeout(() => {
        restoringFromStorageRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Salva filtros no localStorage sempre que mudarem
  useEffect(() => {
    const payload = {
      createdAtStart: createdAtStart
        ? moment(createdAtStart).format("YYYY-MM-DD")
        : "",
      createdAtEnd: createdAtEnd
        ? moment(createdAtEnd).format("YYYY-MM-DD")
        : "",
      status: watch("status") || "",
      typePayment: watch("typePayment") || "",
      typeCustomer: watch("typeCustomer") || "",
      responsibleId: watch("responsibleId") || "",
      isOnline: watch("isOnline") || "",
      selectedRepresentative:
        userPermission === "POWER_USER" ? selectedRepresentative : undefined,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchName,
    nameParam,
    createdAtStart,
    createdAtEnd,
    watch("status"),
    watch("typePayment"),
    watch("typeCustomer"),
    watch("responsibleId"),
    watch("isOnline"),
    selectedRepresentative,
    userPermission,
  ]);

  // Persiste o estado aberto/fechado
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_OPEN_KEY, JSON.stringify(isFiltersOpen));
    } catch {}
  }, [isFiltersOpen]);

  // ===== Queries =====
  const { isLoading: isLoadingListCustomers, data: listCustomers } = useQuery(
    [
      "listCustomers",
      currentPage,
      nameParam,
      createdAtStart ? moment(createdAtStart).format("YYYY-MM-DD") : "",
      createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "",
      watch("status"),
      watch("typePayment"),
      watch("typeCustomer"),
      watch("responsibleId"),
      watch("isOnline"),
      selectedRepresentative,
    ],
    async () => {
      const start = createdAtStart
        ? moment(createdAtStart).format("YYYY-MM-DD")
        : "";
      const end = createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "";

      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?page=${currentPage}` +
          `&search=${encodeURIComponent(nameParam || "")}` +
          `&createdAtStart=${start}` +
          `&createdAtEnd=${end}` +
          `&status=${watch("status") || ""}` +
          `&typeCustomer=${watch("typeCustomer") || ""}` +
          `&typePayment=${watch("typePayment") || ""}` +
          `&responsibleId=${watch("responsibleId") || ""}` +
          `&isOnline=${watch("isOnline") || ""}` +
          `&representativeId=${
            userPermission === "POWER_USER"
              ? selectedRepresentative?.value || ""
              : ""
          }` +
          `&itemsPerPage=${itemsPerPage}`
      );

      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);

      return data.data.data;
    }
  );

  const { isLoading: isLoadingListRepresentatives, data: listRepresentatives } =
    useQuery(
      ["listRepresentatives"],
      async () => {
        const { data } = await axios.get(
          `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
        );
        const representatives = [
          { label: "Todos os representantes", value: "" },
          { label: "Relinq Score", value: 1 },
          ...data.data.data.map((representative) => ({
            value: representative.id,
            label: representative?.name || representative?.socialReason,
          })),
        ];
        return representatives;
      },
      { enabled: userPermission === "POWER_USER" }
    );

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );
    return data.data.data;
  });

  const { isLoading: isLoadingUsers, data: users } = useQuery(
    ["usersAll", user],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_USERS}?itemsPerPage=${-1}&companyId=${
          user?.companyId
        }&status=1`
      );
      return (data?.data?.data || []).map((u) => ({
        id: u.id,
        name: u.name || u.email || `Usuário ${u.id}`,
      }));
    }
  );

  // manter label coerente se veio só id na URL
  useEffect(() => {
    if (
      userPermission === "POWER_USER" &&
      listRepresentatives &&
      selectedRepresentative?.value &&
      !selectedRepresentative?.label
    ) {
      const found = listRepresentatives.find(
        (r) => String(r.value) === String(selectedRepresentative.value)
      );
      if (found) {
        setSelectedRepresentative(found);
        setSearchParams((prev) => {
          const obj = Object.fromEntries(prev.entries());
          obj.representativeName = found.label;
          return obj;
        });
      }
    }
  }, [
    listRepresentatives,
    selectedRepresentative?.value,
    userPermission,
    setSearchParams,
  ]);

  // helper para aplicar filtros na URL mantendo layout
  const applyFiltersToUrl = useCallback(
    (overrides = {}) => {
      if (restoringFromStorageRef.current) return; // evita loop na restauração
      const start = createdAtStart
        ? moment(createdAtStart).format("YYYY-MM-DD")
        : "";
      const end = createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "";
      const base = Object.fromEntries(searchParams.entries());
      const next = {
        ...base,
        page: "1",
        search: nameParam || "",
        createdAtStart: start || "",
        createdAtEnd: end || "",
        status: watch("status") || "",
        typePayment: watch("typePayment") || "",
        typeCustomer: watch("typeCustomer") || "",
        responsibleId: watch("responsibleId") || "",
        isOnline: watch("isOnline") || "",
        ...(userPermission === "POWER_USER"
          ? {
              representativeId: selectedRepresentative?.value || "",
              representativeName: selectedRepresentative?.label || "",
            }
          : { representativeId: "", representativeName: "" }),
        ...overrides,
      };
      Object.keys(next).forEach((k) => {
        if (next[k] === "" || next[k] == null) delete next[k];
      });
      setSearchParams(next);
      setCurrentPage(1);
    },
    [
      createdAtStart,
      createdAtEnd,
      nameParam,
      searchParams,
      selectedRepresentative,
      setSearchParams,
      setCurrentPage,
      userPermission,
      watch,
    ]
  );

  // busca por texto -> atualiza URL
  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
    applyFiltersToUrl({ search: searchName });
  };

  // selects -> sincroniza URL (layout intacto)
  useEffect(() => {
    applyFiltersToUrl(); /* eslint-disable-next-line */
  }, [
    watch("status"),
    watch("typePayment"),
    watch("typeCustomer"),
    watch("responsibleId"),
    watch("isOnline"),
  ]);

  // período -> sincroniza URL
  useEffect(() => {
    const start = createdAtStart
      ? moment(createdAtStart).format("YYYY-MM-DD")
      : "";
    const end = createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "";
    const obj = Object.fromEntries(searchParams.entries());
    const next = { ...obj, page: "1" };
    if (start) next.createdAtStart = start;
    else delete next.createdAtStart;
    if (end) next.createdAtEnd = end;
    else delete next.createdAtEnd;
    setSearchParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAtStart, createdAtEnd]);

  useEffect(() => {
    applyFiltersToUrl();
  }, [createdAtStart, createdAtEnd]); // eslint-disable-line

  // representante -> sincroniza URL
  useEffect(() => {
    if (userPermission !== "POWER_USER") return;
    const obj = Object.fromEntries(searchParams.entries());
    if (selectedRepresentative?.value) {
      obj.representativeId = selectedRepresentative.value;
      obj.representativeName = selectedRepresentative.label || "";
    } else {
      delete obj.representativeId;
      delete obj.representativeName;
    }
    obj.page = "1";
    setSearchParams(obj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRepresentative?.value,
    selectedRepresentative?.label,
    userPermission,
  ]);

  const handleEditPermissionQueries = (company) => {
    setSelectedCompany(company);
    setIsDrawerPermissionQueries((prevState) => !prevState);
  };

  const blockUnlockCustomer = async (type = 0) => {
    try {
      setIsLoadingBlock(true);
      await axios.put(`${ADMIN.GET_COMPANY}/${selectedCompany.id}`, {
        status: type,
      });
      queryClient.invalidateQueries(["listCustomers", currentPage]);
      setOpenModalBlock(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingBlock(false);
    }
  };

  const clearFilters = () => {
    setSearchName("");
    setNameParam("");
    setValue("status", "");
    setValue("typePayment", "");
    setValue("typeCustomer", "");
    setValue("responsibleId", "");
    setValue("isOnline", "");
    setCreatedAtRange([null, null]);
    setSelectedRepresentative({ label: "Todos os representantes", value: "" });
    setCurrentPage(1);
    setSearchParams({ page: "1" });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  // ===== UI helpers =====
  const activeFilters = useMemo(() => {
    const entries = [];
    if (searchName)
      entries.push({
        key: "search",
        tone: "slate",
        label: `Busca: ${searchName}`,
      });
    if (createdAtStart || createdAtEnd)
      entries.push({
        key: "date",
        tone: "slate",
        label: `${
          createdAtStart ? moment(createdAtStart).format("DD/MM/YYYY") : "—"
        } – ${createdAtEnd ? moment(createdAtEnd).format("DD/MM/YYYY") : "—"}`,
      });
    if (watch("typeCustomer"))
      entries.push({
        key: "typeCustomer",
        tone: "indigo",
        label: watch("typeCustomer") === "CPF" ? "Física" : "Jurídica",
      });
    if (watch("typePayment"))
      entries.push({
        key: "typePayment",
        tone: "blue",
        label: watch("typePayment") === "PRE" ? "Pré-Pago" : "Pós-Pago",
      });
    if (String(watch("status")) !== "")
      entries.push({
        key: "status",
        tone: "emerald",
        label:
          Number(watch("status")) === 1
            ? "Ativo"
            : Number(watch("status")) === 0
            ? "Bloqueado"
            : "Inadimplente",
      });
    if (watch("responsibleId"))
      entries.push({
        key: "responsibleId",
        tone: "amber",
        label: `Resp. #${watch("responsibleId")}`,
      });
    if (String(watch("isOnline")) !== "")
      entries.push({
        key: "isOnline",
        tone: "fuchsia",
        label: watch("isOnline") === "1" ? "Online" : "Offline",
      });
    if (userPermission === "POWER_USER" && selectedRepresentative?.value)
      entries.push({
        key: "representativeId",
        tone: "slate",
        label: selectedRepresentative?.label,
      });
    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchName,
    createdAtStart,
    createdAtEnd,
    watch("status"),
    watch("typePayment"),
    watch("typeCustomer"),
    watch("responsibleId"),
    watch("isOnline"),
    selectedRepresentative,
    userPermission,
  ]);

  const activeFiltersCount = activeFilters.length;

  const removeSingleFilter = (key) => {
    switch (key) {
      case "search":
        setSearchName("");
        setNameParam("");
        break;
      case "date":
        setCreatedAtRange([null, null]);
        break;
      case "typeCustomer":
        setValue("typeCustomer", "");
        break;
      case "typePayment":
        setValue("typePayment", "");
        break;
      case "status":
        setValue("status", "");
        break;
      case "responsibleId":
        setValue("responsibleId", "");
        break;
      case "isOnline":
        setValue("isOnline", "");
        break;
      case "representativeId":
        setSelectedRepresentative({
          label: "Todos os representantes",
          value: "",
        });
        break;
      default:
        break;
    }
    applyFiltersToUrl();
  };

  // ==== Presets de período ====
  const applyDatePreset = (preset) => {
    const now = new Date();
    const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    if (preset === "7d")
      setCreatedAtRange([moment(now).subtract(7, "days").toDate(), now]);
    if (preset === "30d")
      setCreatedAtRange([moment(now).subtract(30, "days").toDate(), now]);
    if (preset === "mtd") setCreatedAtRange([startOf(now), now]);
    if (preset === "prevM") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endPrev = new Date(now.getFullYear(), now.getMonth(), 0);
      setCreatedAtRange([prev, endPrev]);
    }
  };

  // ==== Saved Views (salvar combinação atual de filtros) ====
  const [views, setViews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_VIEWS_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const saveCurrentView = () => {
    const name = prompt("Nome da visualização:");
    if (!name) return;
    const v = {
      id: String(Date.now()),
      name,
      data: {
        searchName,
        createdAtStart: createdAtStart
          ? moment(createdAtStart).format("YYYY-MM-DD")
          : "",
        createdAtEnd: createdAtEnd
          ? moment(createdAtEnd).format("YYYY-MM-DD")
          : "",
        status: watch("status") || "",
        typePayment: watch("typePayment") || "",
        typeCustomer: watch("typeCustomer") || "",
        responsibleId: watch("responsibleId") || "",
        isOnline: watch("isOnline") || "",
        representative:
          userPermission === "POWER_USER" ? selectedRepresentative : undefined,
      },
    };
    const next = [v, ...views].slice(0, 10);
    setViews(next);
    try {
      localStorage.setItem(STORAGE_VIEWS_KEY, JSON.stringify(next));
    } catch {}
  };
  const applyView = (v) => {
    if (!v) return;
    setSearchName(v.data.searchName || "");
    setNameParam(v.data.searchName || "");
    setCreatedAtRange([
      v.data.createdAtStart
        ? moment(v.data.createdAtStart, "YYYY-MM-DD").toDate()
        : null,
      v.data.createdAtEnd
        ? moment(v.data.createdAtEnd, "YYYY-MM-DD").toDate()
        : null,
    ]);
    setValue("status", v.data.status || "");
    setValue("typePayment", v.data.typePayment || "");
    setValue("typeCustomer", v.data.typeCustomer || "");
    setValue("responsibleId", v.data.responsibleId || "");
    setValue("isOnline", v.data.isOnline || "");
    if (userPermission === "POWER_USER")
      setSelectedRepresentative(
        v.data.representative || { label: "Todos os representantes", value: "" }
      );
    applyFiltersToUrl();
  };
  const deleteView = (id) => {
    const next = views.filter((x) => x.id !== id);
    setViews(next);
    try {
      localStorage.setItem(STORAGE_VIEWS_KEY, JSON.stringify(next));
    } catch {}
  };

  return (
    <>
      {/* Botão principal */}
      <div className="flex justify-end mb-3 mt-2">
        <Link
          to="/admin/new-customer"
          className="bg-primary ml-auto md:ml-0 mb-5 md:mb-0 fixed md:relative bottom-4 md:bottom-0 right-6 md:right-0 z-10 md:z-0 rounded-full md:rounded-md shadow-md h-14 md:h-auto w-14 md:w-auto flex md:block justify-center items-center px-4 py-2 text-white"
        >
          <IoMdAdd className="block md:hidden text-2xl" />
          <span className="hidden md:block">Adicionar cliente</span>
        </Link>
      </div>

      {/* ====== FILTROS PRO ====== */}
      <form onSubmit={handleSearchName}>
        <Section
          title={
            <>
              Filtros
              {activeFiltersCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-[11px] px-2 py-0.5 rounded-full bg-gray-900/5 text-gray-700 border border-gray-200">
                  {activeFiltersCount}
                </span>
              )}
            </>
          }
          right={
            <div className="flex items-center gap-2">
              {/* Views rápidas */}
              {views.length > 0 && (
                <div className="hidden md:flex items-center gap-2">
                  {views.slice(0, 3).map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                      onClick={() => applyView(v)}
                      title={`Aplicar “${v.name}”`}
                    >
                      <span className="inline-flex items-center gap-1">
                        <MdCheckCircleOutline /> {v.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
        >
          {/* Barra superior: busca e ações rápidas */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row gap-2">
              <Button
                color="light"
                type="button"
                size="xs"
                onClick={() => setIsFiltersOpen((s) => !s)}
                title={isFiltersOpen ? "Recolher" : "Expandir"}
                className="text-2xs"
              >
                <span className="flex items-center gap-1">
                  {isFiltersOpen ? "Recolher " : "Expandir "}
                  filtro avançado
                  <MdKeyboardArrowDown
                    className={`transition-transform duration-200 ${
                      isFiltersOpen ? "rotate-180" : "rotate-0"
                    }`}
                    size={18}
                  />
                </span>
              </Button>
              <div className="flex-1 flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-3 py-2 focus-within:border-indigo-300">
                <IoMdSearch className="opacity-60 shrink-0" />
                <input
                  type="text"
                  placeholder="Buscar por nome, telefone, e-mail ou documento…"
                  className="bg-transparent border-0 flex-1 text-sm py-1 focus:outline-none focus:ring-0 placeholder:text-gray-500"
                  onChange={(e) => setSearchName(e.target.value)}
                  value={searchName}
                />
                {searchName && (
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      setSearchName("");
                      setNameParam("");
                      applyFiltersToUrl({ search: "" });
                    }}
                  >
                    Limpar
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-secondary min-w-24"
                  type="submit"
                  title="Aplicar busca"
                >
                  <div className="flex items-center gap-1">
                    <MdOutlineBolt /> Buscar
                  </div>
                </Button>
                <Button
                  color="light"
                  size="sm"
                  onClick={clearFilters}
                  className="whitespace-nowrap"
                  title="Limpar todos os filtros"
                  type="button"
                >
                  <div className="flex items-center gap-1">
                    <MdOutlineRestore /> Limpar
                  </div>
                </Button>
              </div>
            </div>

            {/* Chips dos filtros ativos */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((f, i) => (
                  <Chip
                    key={`${f.key}-${i}`}
                    label={f.label}
                    tone={f.tone}
                    onClear={() => removeSingleFilter(f.key)}
                  />
                ))}
              </div>
            )}

            {/* Conteúdo colapsável */}
            <div
              className={`overflow-hidden transition-all duration-300 ${
                isFiltersOpen
                  ? "max-h-[1400px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              {/* Grid de filtros */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-2">
                <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-3">
                  <DateRangeInput
                    label={
                      <span className="text-[13px] font-medium text-gray-700">
                        Data de cadastro
                      </span>
                    }
                    startDate={createdAtStart}
                    endDate={createdAtEnd}
                    onChange={setCreatedAtRange}
                    register={register}
                    nameStart="createdAtStart"
                    nameEnd="createdAtEnd"
                    maxDate={new Date()}
                  />
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3">
                  <SelectInput
                    register={register}
                    label="Tipo de cliente"
                    name="typeCustomer"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="CPF">Física</option>
                    <option value="CNPJ">Jurídica</option>
                  </SelectInput>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3">
                  <SelectInput
                    register={register}
                    label="Tipo de pagamento"
                    name="typePayment"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="PRE">Pré-Pago</option>
                    <option value="POS">Pós-Pago</option>
                  </SelectInput>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3">
                  <SelectInput
                    register={register}
                    label="Status do cliente"
                    name="status"
                  >
                    <option value="">Todos os status</option>
                    <option value={1}>Ativo</option>
                    <option value={0}>Bloqueado</option>
                    <option value={-1}>Inadimplente</option>
                  </SelectInput>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3">
                  <SelectInput
                    register={register}
                    label="Responsável pelo Cliente"
                    name="responsibleId"
                    disabled={isLoadingUsers}
                  >
                    <option value="">Todos os responsáveis</option>
                    {(users || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3">
                  <SelectInput
                    register={register}
                    label="Cadastro Online"
                    name="isOnline"
                  >
                    <option value="">Todos</option>
                    <option value="1">Sim</option>
                    <option value="0">Não</option>
                  </SelectInput>
                </div>

                {userPermission === "POWER_USER" && (
                  <div className="bg-white rounded-xl border border-gray-200 p-3">
                    <AutoCompleteInput
                      options={listRepresentatives}
                      label="Representante"
                      onChange={(rep) => setSelectedRepresentative(rep)}
                      value={selectedRepresentative?.value}
                      isLoading={isLoadingListRepresentatives}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>
      </form>

      {/* Tabela */}
      {listCustomers?.length > 0 || isLoadingListCustomers ? (
        <>
          <small
            className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
            style={{ fontSize: 10 }}
          >
            <span>
              Deslize para o lado para visualizar todas as informações
            </span>{" "}
            <FaArrowsLeftRight />
          </small>

          <div className="overflow-x-auto shadow-sm rounded-xl bg-white mt-0 md:mt-5 border border-[#ece7e3]">
            <Table striped={false} className="text-xs xl:text-sm">
              <Table.Head className="uppercase text-[11px] tracking-wide">
                <Table.HeadCell className="bg-gray-200 text-[#6b5f57]">
                  Cliente
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-200 text-[#6b5f57]">
                  Cadastro
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-200 text-[#6b5f57]">
                  Representante
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-200 text-[#6b5f57] whitespace-nowrap">
                  Tipo Pgto.
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-200 text-[#6b5f57]">
                  Ações
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y divide-gray-200">
                {!isLoadingListCustomers ? (
                  listCustomers?.length > 0 ? (
                    listCustomers.map((customer, index) => (
                      <Table.Row
                        key={index}
                        className={`transition ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-[#fcfcff]`}
                      >
                        {/* Cliente */}
                        <Table.Cell className="text-gray-700 py-2 font-medium">
                          <div>
                            {customer.typeCustomer === "CPF"
                              ? customer.name
                              : customer.socialReason}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {customer.typeCustomer}: {customer.document} -{" "}
                            <span
                              className={`inline-block font-semibold ${
                                customer.status === 1
                                  ? "text-green-500"
                                  : customer.status === 0
                                  ? "text-red-500"
                                  : "text-red-500"
                              }`}
                            >
                              {customer.status === 1
                                ? "Ativo"
                                : customer.status === 0
                                ? "Bloqueado"
                                : "Inadimplente"}
                            </span>
                          </div>
                        </Table.Cell>

                        {/* Cadastro */}
                        <Table.Cell className="text-center text-gray-700 py-2">
                          <div>
                            {moment(customer.createdAt).format("DD/MM/yyyy")}
                          </div>
                          <div className="text-[11px] text-gray-400 -mt-1">
                            {moment(customer.createdAt).fromNow()}
                          </div>
                        </Table.Cell>

                        {/* Representante */}
                        <Table.Cell className="text-center text-gray-700 py-2">
                          {customer?.ParentCompany?.name ||
                          customer?.ParentCompany?.socialReason ? (
                            <span
                              className="block truncate max-w-[160px] mx-auto text-sm"
                              title={
                                customer.ParentCompany.name ||
                                customer.ParentCompany.socialReason
                              }
                            >
                              {customer.ParentCompany.name ||
                                customer.ParentCompany.socialReason}
                            </span>
                          ) : (
                            "-"
                          )}
                        </Table.Cell>

                        {/* Tipo Pgto. */}
                        <Table.Cell className="text-center py-2">
                          <div className="flex items-center justify-center gap-1.5">
                            <div
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${
                                customer.typePayment === "PRE"
                                  ? "text-green-700 bg-green-50 border-green-200"
                                  : "text-blue-700 bg-blue-50 border-blue-200"
                              }`}
                            >
                              {customer.typePayment === "PRE" ? (
                                <>Pré-Pago - {formatToReal(customer?.credit)}</>
                              ) : (
                                <>
                                  Pós-Pago{" "}
                                  {customer?.vcm
                                    ? `- vcm: ${formatToReal(customer?.vcm)}`
                                    : ""}
                                </>
                              )}
                            </div>
                          </div>
                        </Table.Cell>

                        {/* Ações */}
                        <Table.Cell className="text-center py-2">
                          <div className="flex justify-center items-center gap-1.5">
                            <button
                              data-tooltip-id="tooltip-permissions"
                              data-tooltip-content="Editar permissões de consulta"
                              onClick={() =>
                                handleEditPermissionQueries(customer)
                              }
                              className="p-2.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition duration-200"
                            >
                              <TbLockSearch size={18} />
                            </button>
                            <Tooltip id="tooltip-permissions" place="top" />

                            <button
                              data-tooltip-id="tooltip-view-customer"
                              data-tooltip-content="Visualizar detalhes do cliente"
                              onClick={() =>
                                navigate(`/admin/customers/${customer?.uuid}`)
                              }
                              className="p-2.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700 transition duration-200"
                            >
                              <MdOutlineRemoveRedEye size={18} />
                            </button>
                            <Tooltip id="tooltip-view-customer" place="top" />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={5}
                        className="text-center py-4 text-gray-400"
                      >
                        Nenhum cliente encontrado.
                      </Table.Cell>
                    </Table.Row>
                  )
                ) : (
                  <TableLoading cols={5} />
                )}
              </Table.Body>
            </Table>
          </div>

          {/* Rodapé: contagem + paginação */}
          {totalCount > 0 && (
            <div className="mt-3 text-center text-sm text-gray-600">
              Exibindo {(currentPage - 1) * 20 + 1}–
              {Math.min(currentPage * 20, totalCount)} de {totalCount} registros
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </>
      ) : (
        <EmptyTable icon="person_off">Nenhum cliente cadastrado</EmptyTable>
      )}

      {/* Drawers & Modal */}
      <Drawer
        title={`Visualizar faturamento`}
        onClose={setIsDrawerViewInvoice}
        isOpen={isDrawerViewInvoice}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <CompanyInvoiceForm
          company={selectedCompany}
          onClose={setIsDrawerViewInvoice}
          isDrawerOpen={isDrawerViewInvoice}
          companyType="CUSTOMER"
        />
      </Drawer>

      <Modal
        show={isDrawerPermissionQueries}
        onClose={() => setIsDrawerPermissionQueries(false)}
        size="4xl"
      >
        <Modal.Header>Permissões</Modal.Header>
        <Modal.Body>
          <CompanyQueriesForm
            company={selectedCompany}
            categories={categories}
            onClose={() => setIsDrawerPermissionQueries(false)}
            fetchCompanies={() =>
              queryClient.invalidateQueries(["listCustomers", currentPage])
            }
          />
        </Modal.Body>
      </Modal>

      <Drawer
        title={`Editar cliente`}
        onClose={setIsDrawerEditCustomer}
        isOpen={isDrawerEditCustomer}
        className={"w-full md:w-4/6 lg:w-3/6"}
      >
        <CompanyEditForm
          company={selectedCompany}
          onClose={setIsDrawerEditCustomer}
          categories={categories}
          fetchCompanies={() =>
            queryClient.invalidateQueries(["listCustomers", currentPage])
          }
        />
      </Drawer>

      <ModalDelete
        title={`Deseja ${
          typeBlock === 0 ? "bloquear" : "desbloquear"
        } este cliente?`}
        isOpen={isOpenModalBlock}
        setOpenModal={setOpenModalBlock}
        isLoading={isLoadingBlock}
        handleDelete={() => blockUnlockCustomer(typeBlock)}
      />
    </>
  );
};

export default AdminCustomersTable;
